// src/components/TiKaneSenpModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { AsyncPaginate } from 'react-select-async-paginate';
import clientService from '../services/clientService';

const PAGE_SIZE = 20;

// normalise (accents/casse)
const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

export default function TiKaneSenpModal({
  show,
  onHide,
  onSave,
  initialData = null,
}) {
  const init = initialData ?? {};
  const isEdit = !!init.id;

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      clientId: init.clientId ?? null,
      status: init.status ?? 'ACTIVE',
    },
  });

  const [allClients, setAllClients] = useState([]);
  const [error, setError] = useState('');

  // Charger les clients quand on ouvre la modal
  useEffect(() => {
    if (!show) return;

    setError('');
    reset({
      clientId: init.clientId ?? null,
      status: init.status ?? 'ACTIVE',
    });

    clientService
      .list()
      .then(setAllClients)
      .catch(() => setAllClients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, init.id, init.clientId, init.status, reset]);

  // Helpers options clients
  const toOption = (c) => ({
    value: c.id,
    label: `${c.firstName || ''} ${c.lastName || ''} — ${c.nifCin || 'NIF/CIN ?'}`.trim(),
  });

  const selectedClientOption = useMemo(() => {
    const currentId = watch('clientId');
    if (!currentId) return null;
    const found = allClients.find((c) => c.id === currentId);
    return found ? toOption(found) : { value: currentId, label: `Client #${currentId}` };
  }, [allClients, watch('clientId')]);

  const loadClientOptions = async (inputValue, _loaded, { page }) => {
    const q = normalize(inputValue);
    const filtered = allClients.filter((c) => {
      const hay = normalize(
        `${c.firstName || ''} ${c.lastName || ''} ${c.nifCin || ''} ${c.email || ''} ${c.phone || ''}`
      );
      return hay.includes(q);
    });

    const start = (page - 1) * PAGE_SIZE;
    const slice = filtered.slice(start, start + PAGE_SIZE);
    const options = slice.map(toOption);
    const hasMore = start + PAGE_SIZE < filtered.length;

    return {
      options,
      hasMore,
      additional: { page: page + 1 },
    };
  };

  const onSubmit = (data) => {
    if (!data.clientId) {
      setError('Veuillez sélectionner un client.');
      return;
    }
    setError('');
    // on renvoie l’ID du compte (si édition) + clientId + status
    onSave({
      id: init.id,
      clientId: data.clientId,
      status: data.status,
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit
              ? 'Modifier un compte Ti Kanè Senp (client adulte)'
              : 'Nouveau compte Ti Kanè Senp (client adulte)'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="g-3">
            <Col md={8}>
              <Form.Group controlId="clientId">
                <Form.Label>Client *</Form.Label>
                <Controller
                  name="clientId"
                  control={control}
                  rules={{ required: 'Client requis' }}
                  render={({ field }) => (
                    <AsyncPaginate
                      value={selectedClientOption}
                      onChange={(opt) => field.onChange(opt?.value ?? null)}
                      loadOptions={loadClientOptions}
                      additional={{ page: 1 }}
                      debounceTimeout={300}
                      placeholder="Rechercher par nom, NIF/CIN, téléphone…"
                      isClearable
                    />
                  )}
                />
                {errors.clientId && (
                  <Form.Text className="text-danger">
                    {errors.clientId.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="status">
                <Form.Label>Statut</Form.Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="CLOSED">CLOSED</option>
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button variant="primary" type="submit">
            {isEdit ? 'Enregistrer les modifications' : 'Créer le compte'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
